/**
 * useUnreadCommentCount Hook
 *
 * Provides real-time unread comment count for the current user.
 * Subscribes to task_note_comment_reads table for instant updates.
 *
 * Features:
 * - Fetches unread count using get_unread_comment_count() RPC
 * - Real-time updates via Supabase subscription
 * - Mark all comments as read functionality
 * - Auto-refetch on realtime events
 *
 * Usage:
 * const { unreadCount, markAllAsRead, isLoading } = useUnreadCommentCount();
 */

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const useUnreadCommentCount = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Query for unread count (using unified comment system)
  const {
    data: unreadCount = 0,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["unread-comment-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;

      logger.info(
        "[useUnreadCommentCount] Fetching unread count for user:",
        user.id
      );

      // Use new unified RPC function
      const { data, error } = await supabase.rpc(
        "get_unread_comment_count_v2",
        {
          target_user_id: user.id,
        }
      );

      if (error) {
        logger.error("[useUnreadCommentCount] Error fetching count:", error);
        return 0;
      }

      logger.info("[useUnreadCommentCount] Unread count:", data);
      return data || 0;
    },
    enabled: !!user,
    staleTime: 60000, // 1 minute - reasonable for notification counts
    refetchOnWindowFocus: true, // Refetch when user returns to app
  });

  // Subscribe to realtime updates on unified therapist_comment_reads table
  useEffect(() => {
    if (!user) return;

    logger.info(
      "[useUnreadCommentCount] Setting up realtime subscription (unified table)"
    );

    // Debounce invalidation to prevent rapid refetches
    let invalidationTimeout: ReturnType<typeof setTimeout> | null = null;
    const lastInvalidation = { timestamp: 0 };
    const DEBOUNCE_MS = 1000; // 1 second debounce

    const debouncedInvalidate = () => {
      const now = Date.now();
      // Prevent duplicate invalidations within debounce window
      if (now - lastInvalidation.timestamp < DEBOUNCE_MS) {
        logger.info(
          "[useUnreadCommentCount] Skipping duplicate invalidation (debounced)"
        );
        return;
      }

      if (invalidationTimeout) {
        clearTimeout(invalidationTimeout);
      }

      invalidationTimeout = setTimeout(() => {
        lastInvalidation.timestamp = Date.now();
        logger.info("[useUnreadCommentCount] Invalidating queries (debounced)");
        queryClient.invalidateQueries({
          queryKey: ["unread-comment-count", user.id],
        });
      }, 300); // Wait 300ms for additional events
    };

    const channel = supabase
      .channel(`unified-comments:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT, UPDATE, DELETE events
          schema: "public",
          table: "therapist_comments", // Listen for new comments
        },
        (payload) => {
          logger.info(
            "[useUnreadCommentCount] Comment change received:",
            payload.eventType,
            payload.new?.id || payload.old?.id
          );
          debouncedInvalidate();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT and DELETE events
          schema: "public",
          table: "therapist_comment_reads", // Listen for read status changes
        },
        (payload) => {
          logger.info(
            "[useUnreadCommentCount] Read status change received:",
            payload.eventType,
            payload.new?.id || payload.old?.id
          );
          debouncedInvalidate();
        }
      )
      .subscribe((status) => {
        logger.info("[useUnreadCommentCount] Subscription status:", status);
      });

    return () => {
      logger.info("[useUnreadCommentCount] Cleaning up subscription");
      if (invalidationTimeout) {
        clearTimeout(invalidationTimeout);
      }
      channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user object excluded, using user?.id
  }, [user?.id, queryClient]);

  // Function to mark all comments as read
  const markAllAsRead = async () => {
    if (!user) {
      logger.warn(
        "[useUnreadCommentCount] Cannot mark all as read: user not authenticated"
      );
      return;
    }

    logger.info("[useUnreadCommentCount] Marking all comments as read");

    try {
      const { error } = await supabase.rpc("mark_all_comments_read");

      if (error) {
        throw error;
      }

      logger.info("[useUnreadCommentCount] Successfully marked all as read");

      // Show success toast
      toast.success(
        t("notifications.allMarkedRead", "Alle kommentarer markeret som læst")
      );

      // Invalidate query to update UI immediately
      queryClient.invalidateQueries(["unread-comment-count", user.id]);
    } catch (error) {
      logger.error("[useUnreadCommentCount] Error marking all as read:", error);
      toast.error(t("common.error", "Fejl"));
      throw error;
    }
  };

  // Log errors
  if (error) {
    logger.error("[useUnreadCommentCount] Query error:", error);
  }

  return {
    unreadCount,
    markAllAsRead,
    isLoading,
  };
};
